import { GAME_MODES, type GameMode, TEAM_SIZE } from '../../schemas/game';

/**
 * Sit-out rotation for "Next game" (RULINGS gaps; the 2v2 example where one person sits out and
 * keeps score). The sitter is preselected as keeper, so the book passes in one tap.
 */

export interface SitterPlayer {
  id: string;
  /** Games this player has sat out so far (today's run). */
  satOut: number;
  /** Games this player has played so far. */
  played: number;
  /** Points scored in the game that just ended. Lower sits first when everything else is level. */
  points?: number;
  /** Side in the game that just ended. */
  team?: 'A' | 'B';
}

export type SitRule = 'rotate' | 'winners_stay';

export interface SitterInput {
  players: readonly SitterPlayer[];
  /** How many have to sit (present minus seats). */
  sitCount: number;
  /** 'rotate' (default): fewest sit-outs sit next. 'winners_stay': the losing side sits first. */
  rule?: SitRule;
  /** The side that lost the last game, for 'winners_stay'. null on a tie. */
  losingTeam?: 'A' | 'B' | null;
  /** Who sat in the last game, so nobody sits twice in a row while others have not sat. */
  justSat?: readonly string[];
}

const byId = (a: SitterPlayer, b: SitterPlayer) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** The player ids who sit out the next game, best candidate first. Empty when sitCount is 0. */
export function nextSitter(input: SitterInput): string[] {
  const n = Math.max(0, Math.min(input.sitCount, input.players.length));
  if (n === 0) return [];
  const just = new Set(input.justSat ?? []);
  const rotate = (a: SitterPlayer, b: SitterPlayer) =>
    a.satOut - b.satOut ||
    b.played - a.played ||
    Number(just.has(a.id)) - Number(just.has(b.id)) ||
    (a.points ?? 0) - (b.points ?? 0) ||
    byId(a, b);

  if (input.rule === 'winners_stay' && input.losingTeam) {
    const losers = input.players.filter((p) => p.team === input.losingTeam);
    const sorted = [...losers].sort(
      (a, b) => Number(just.has(a.id)) - Number(just.has(b.id)) || (a.points ?? 0) - (b.points ?? 0) || byId(a, b),
    );
    const picked = sorted.slice(0, n).map((p) => p.id);
    if (picked.length === n) return picked;
    const rest = input.players.filter((p) => !picked.includes(p.id)).sort(rotate);
    return [...picked, ...rest.slice(0, n - picked.length).map((p) => p.id)];
  }
  return [...input.players]
    .sort(rotate)
    .slice(0, n)
    .map((p) => p.id);
}

// ---------------------------------------------------------------------------------------------
// Odd counts: which format fits N players, and who sits when (gameplay.md 2.3)
// ---------------------------------------------------------------------------------------------

/** One way to spend N players on one court. */
export interface RotationFormat {
  mode: GameMode;
  /** Players on court (twice the team size). */
  seats: number;
  /** Players who sit and keep the book. 0 when everybody plays. */
  sitCount: number;
  /** 'winners_stay' is the natural fit when two play and the rest queue; 'rotate' elsewhere. */
  rule: SitRule;
}

/**
 * The formats that fit `n` players on one court, best first. The first is the biggest game that
 * seats everyone it can (3 players is a 1v1 with a sitter, 5 is a 2v2 with a sitter, 7 is a 3v3
 * with a sitter, 9 is a 4v4 with a sitter). The second, when there is one, is the next size down
 * with more people on the bench, for a crew that wants shorter turns.
 */
export function rotationFormats(n: number): RotationFormat[] {
  if (n < 2) return [];
  const fits = GAME_MODES.filter((m) => TEAM_SIZE[m] * 2 <= n).reverse();
  const out: RotationFormat[] = [];
  fits.forEach((mode, i) => {
    const seats = TEAM_SIZE[mode] * 2;
    const sitCount = n - seats;
    // The second option only makes sense with a small bench.
    if (i > 1 || (i === 1 && sitCount > 3)) return;
    out.push({ mode, seats, sitCount, rule: seats === 2 && sitCount > 0 ? 'winners_stay' : 'rotate' });
  });
  return out;
}

export interface ScheduledGame {
  /** 1-based. */
  game: number;
  playing: string[];
  sitting: string[];
}

/**
 * Who sits in each of the next `games` games with the plain rotation (nobody twice until
 * everybody has sat once). `history` seeds the counters with what already happened today.
 * Winners-stay needs results, so it is decided game by game with `nextSitter`, not planned here.
 */
export function sitOutSchedule(input: {
  playerIds: readonly string[];
  /** Players on court per game. */
  seats: number;
  games: number;
  history?: {
    satOut: Readonly<Record<string, number>>;
    played: Readonly<Record<string, number>>;
    justSat?: readonly string[];
  };
}): ScheduledGame[] {
  const ids = [...input.playerIds];
  const sitCount = Math.max(0, ids.length - input.seats);
  const satOut = new Map(ids.map((id) => [id, input.history?.satOut[id] ?? 0]));
  const played = new Map(ids.map((id) => [id, input.history?.played[id] ?? 0]));
  let justSat: readonly string[] = input.history?.justSat ?? [];
  const out: ScheduledGame[] = [];
  for (let g = 1; g <= input.games; g++) {
    const sitting = nextSitter({
      players: ids.map((id) => ({ id, satOut: satOut.get(id) ?? 0, played: played.get(id) ?? 0 })),
      sitCount,
      justSat,
    });
    const sit = new Set(sitting);
    const playing = ids.filter((id) => !sit.has(id));
    for (const id of sitting) satOut.set(id, (satOut.get(id) ?? 0) + 1);
    for (const id of playing) played.set(id, (played.get(id) ?? 0) + 1);
    justSat = sitting;
    out.push({ game: g, playing, sitting });
  }
  return out;
}
